import { useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { useInView } from 'react-intersection-observer'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Image from 'components/Common/Image'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { TOP_ATTRACTIONS_SKELETON_COUNT } from 'constants/experience'
import Places from 'constants/places'
import useTopAttractions from 'hooks/Experiences/useTopAttractions'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import { fillArray } from 'lib/array/arrayUtils'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import { setAttractionAttribution } from 'lib/attractions/attractionAttribution'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'

/**
 * Convert an attraction imageUrl to an App.Image object.
 * API may return either a full URL from curation/admin or a plain image ID.
 */
export function toAttractionImage(imageUrl: string): App.Image {
  if (imageUrl.startsWith('http')) return { url: imageUrl }
  return { id: imageUrl }
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  padding-top: ${rem(12)};

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(16)};
    gap: ${rem(20)};
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(6)};
`

const Hint = styled.div`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: ${rem(6)};
  color: ${(props) => props.theme.palette.highlight.primary.darkForeground};
`

const Tile = styled.button`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  flex-shrink: 0;
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: ${(props) => props.theme.borderRadius.S};
  padding: 0;
  background: none;
  text-align: left;
  transition: border-color 0.2s ease;
  overflow: hidden;

  &.selected {
    border-color: ${(props) => props.theme.palette.brand.primary.normal};
  }
`

const TileImage = styled.div`
  width: ${rem(220)};
  height: ${rem(80)};
  overflow: hidden;
  background-color: ${(props) => props.theme.palette.neutral.default.seven};
`

const TileContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(4)};
  width: ${rem(220)};
`

const SkeletonImage = styled(LoadingBox)`
  width: ${rem(220)};
  height: ${rem(80)};
  border-radius: ${(props) => props.theme.borderRadius.S};
`

const SkeletonText = styled(LoadingBox)`
  width: ${rem(160)};
  height: ${rem(16)};
  border-radius: ${(props) => props.theme.borderRadius.XS};
`

const SkeletonCaption = styled(LoadingBox)`
  width: ${rem(200)};
  height: ${rem(12)};
  border-radius: ${(props) => props.theme.borderRadius.XS};
  margin-top: ${rem(4)};
`

interface AttractionTileProps {
  attraction: Experiences.Attraction
  selected: boolean
  onClick: () => void
  onImpression: () => void
}

function AttractionTile({ attraction, selected, onClick, onImpression }: AttractionTileProps) {
  const [ref, inView] = useInView({ triggerOnce: true })

  useEffect(() => {
    if (inView) onImpression()
  }, [inView, onImpression])

  return (
    <Tile
      ref={ref}
      className={clsx({ selected })}
      onClick={onClick}
      aria-pressed={selected}
    >
      <TileImage>
        {attraction.imageUrl && (
          <Image
            image={toAttractionImage(attraction.imageUrl)}
            width={220}
            height={80}
            aspectRatio="11:4"
            quality="good"
            alt={attraction.name}
            loading="lazy"
            fit="center"
            enhancedFormats={false}
          />
        )}
      </TileImage>
      <TileContent>
        <BodyText variant="medium" weight="bold" lineClamp={2}>
          {attraction.name}
        </BodyText>
        <CSSBreakpoint min="desktop">
          {attraction.description && (
            <Caption variant="medium" lineClamp={2} colour="neutral-three">
              {attraction.description}
            </Caption>
          )}
        </CSSBreakpoint>
      </TileContent>
    </Tile>
  )
}

interface Props {
  place?: App.Place;
  selectedAttractionId?: string;
  onAttractionSelect: (attractionId: string | undefined) => void;
  onRenderStateChange?: (isRendered: boolean) => void;
}

function ExperienceSearchTopAttractions({
  place,
  selectedAttractionId,
  onAttractionSelect,
  onRenderStateChange,
}: Props) {
  const intl = useIntl()
  const trackEvent = useTrackExperienceOfferEvent()
  const { attractions, fetching, isCuratedDestination } = useTopAttractions(place)

  const [carouselRef, carouselInView] = useInView({ triggerOnce: true })
  const hasFiredCarouselImpression = useRef(false)

  // Landmarks is an opt-in surface revealed by a toggle on the page (default hidden) and
  // applied overall to both arms. There is no auto-select: the offer list only changes when
  // the customer explicitly picks a landmark (handleAttractionClick below).

  useEffect(() => {
    if (carouselInView && attractions.length > 0 && !hasFiredCarouselImpression.current) {
      hasFiredCarouselImpression.current = true
      trackEvent('ATTRACTION_CAROUSEL_IMPRESSION')
    }
  }, [carouselInView, attractions.length, trackEvent])

  const prevSelectedRef = useRef(selectedAttractionId)
  useEffect(() => {
    prevSelectedRef.current = selectedAttractionId
  }, [selectedAttractionId])

  const handleAttractionClick = useCallback(
    (attraction: Experiences.Attraction) => {
      const isDeselecting = selectedAttractionId === attraction.attractionId
      onAttractionSelect(isDeselecting ? undefined : attraction.attractionId)

      if (!isDeselecting) {
        trackEvent('ATTRACTION_TILE_CLICKED')
        setAttractionAttribution(
          attraction.attractionId,
          attraction.name,
          place?.name ?? '',
        )
      }
    },
    [selectedAttractionId, onAttractionSelect, trackEvent, place?.name],
  )

  const handleTileImpression = useCallback(() => {
    trackEvent('ATTRACTION_TILE_IMPRESSION')
  }, [trackEvent])

  const isAnywhere = place?.id === Places.Anywhere.id

  // The surface is always presented as "Landmarks" (it only renders when the customer
  // opens the Landmarks toggle on the page). Copy pending product sign-off.
  const landmarksTitle = isAnywhere
    ? intl.formatMessage({
        id: 'M3oMQGGg1r1zUJbQFeTm',
        defaultMessage: 'Explore landmarks',
        description:
          'Title for the landmarks section when no specific destination is selected',
      })
    : intl.formatMessage(
        {
          id: 'Ijtnxq0CepFHdlaF6c8z',
          defaultMessage: 'Explore landmarks in {placeName}',
          description: 'Title for the landmarks section in experience search page',
        },
        { placeName: place?.name },
      )

  const title = landmarksTitle

  const landmarksSubtitle = intl.formatMessage({
    id: '1B11DULy1zEANEnabClg',
    defaultMessage: 'Pick a landmark to see the experiences closest to it',
    description: 'Subtitle explaining the landmarks selection behaviour',
  })

  const shouldRender = Boolean(
    place &&
    isCuratedDestination !== false &&
    (fetching || attractions.length > 0),
  )

  useIsomorphicLayoutEffect(() => {
    onRenderStateChange?.(shouldRender)
  }, [onRenderStateChange, shouldRender])

  useIsomorphicLayoutEffect(() => {
    return () => onRenderStateChange?.(false)
  }, [onRenderStateChange])

  if (!shouldRender) {
    return null
  }

  return (
    <Root ref={carouselRef}>
      <Header>
        <BodyText variant="medium" weight="bold">{title}</BodyText>
        {/* The guidance hint is only useful before a landmark is picked; hide it once one is selected. */}
        {!selectedAttractionId && (
          <Hint>
            <LineInfoCircleIcon size="XS" />
            <Caption variant="medium">{landmarksSubtitle}</Caption>
          </Hint>
        )}
      </Header>
      <CardCarousel pageSize="auto" snap="start" gap={12} width={rem(220)}>
        {fetching && attractions.length === 0 &&
          fillArray(TOP_ATTRACTIONS_SKELETON_COUNT).map((id) => (
            <div key={id}>
              <SkeletonImage />
              <TileContent>
                <SkeletonText />
                <CSSBreakpoint min="desktop">
                  <SkeletonCaption />
                </CSSBreakpoint>
              </TileContent>
            </div>
          ))}
        {attractions.length > 0 &&
          attractions.map((attraction) => (
            <AttractionTile
              key={attraction.attractionId}
              attraction={attraction}
              selected={selectedAttractionId === attraction.attractionId}
              onClick={() => handleAttractionClick(attraction)}
              onImpression={handleTileImpression}
            />
          ))}
      </CardCarousel>
    </Root>
  )
}

export default ExperienceSearchTopAttractions
